import React, { MouseEvent, useCallback, useContext, useState } from 'react'
import { Link } from 'react-router-dom'
import {
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    Fade,
    IconButton,
} from '@mui/material'
import {
    EditIcon,
    BlockIcon,
    UnblockIcon,
    ActivateIcon,
    DeleteIcon,
    MoreVertIcon,
} from '../components'
import { translate } from '../i18n'
import { AppContext } from '../context'
import { UserMenuProps } from './UserMenu.types'

export default function UserMenu({
    tenantId,
    user,
    onActivated,
    onBlock,
    onUnblock,
    onDelete,
}: UserMenuProps) {
    const context = useContext(AppContext)
    const itself = user.id === context.session?.user.id
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const onClickedOptions = useCallback((event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
    }, [])

    const onClickedClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    return (
        <>
            <IconButton
                aria-label="options"
                color="default"
                size="small"
                onClick={onClickedOptions}
            >
                <MoreVertIcon />
            </IconButton>
            {anchorEl && (
                <Menu
                    id="tenants-item"
                    anchorEl={anchorEl}
                    keepMounted
                    onClose={onClickedClose}
                    open
                    TransitionComponent={Fade}
                >
                    {itself && (
                        <MenuItem
                            component={Link}
                            to="/profile"
                            onClick={onClickedClose}
                            dense
                        >
                            <ListItemIcon>
                                <EditIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <Typography variant="inherit">
                                {translate('buttons.edit')}
                            </Typography>
                        </MenuItem>
                    )}
                    {!itself &&
                        context.isGrantedAny([
                            'UsersUpdate',
                            'TenantsUsersUpdate',
                        ]) && (
                            <MenuItem
                                component={Link}
                                to={`${
                                    tenantId ? `/tenants/${tenantId}` : ''
                                }/users/${user.id}/edit`}
                                onClick={onClickedClose}
                                dense
                            >
                                <ListItemIcon>
                                    <EditIcon
                                        fontSize="small"
                                        color="primary"
                                    />
                                </ListItemIcon>
                                <Typography variant="inherit">
                                    {translate('buttons.edit')}
                                </Typography>
                            </MenuItem>
                        )}
                    {!itself &&
                        !user.blocked &&
                        user.signup_code &&
                        context.isGrantedAny([
                            'UsersUpdate',
                            'TenantsUsersUpdate',
                        ]) && (
                            <MenuItem onClick={() => onActivated(user)} dense>
                                <ListItemIcon>
                                    <ActivateIcon
                                        fontSize="small"
                                        color="primary"
                                    />
                                </ListItemIcon>
                                <Typography variant="inherit">
                                    {translate('buttons.activate')}
                                </Typography>
                            </MenuItem>
                        )}
                    {!itself &&
                        !user.blocked &&
                        context.isGrantedAny([
                            'UsersBlock',
                            'TenantsUsersBlock',
                        ]) && (
                            <MenuItem onClick={() => onBlock(user)} dense>
                                <ListItemIcon>
                                    <BlockIcon
                                        fontSize="small"
                                        color="primary"
                                    />
                                </ListItemIcon>
                                <Typography variant="inherit">
                                    {translate('buttons.block')}
                                </Typography>
                            </MenuItem>
                        )}
                    {!itself &&
                        user.blocked &&
                        context.isGrantedAny([
                            'UsersBlock',
                            'TenantsUsersBlock',
                        ]) && (
                            <MenuItem onClick={() => onUnblock(user)} dense>
                                <ListItemIcon>
                                    <UnblockIcon
                                        fontSize="small"
                                        color="primary"
                                    />
                                </ListItemIcon>
                                <Typography variant="inherit">
                                    {translate('buttons.unblock')}
                                </Typography>
                            </MenuItem>
                        )}
                    {!itself &&
                        context.isGrantedAny([
                            'UsersDelete',
                            'TenantsUsersDelete',
                        ]) && (
                            <MenuItem onClick={() => onDelete(user)} dense>
                                <ListItemIcon>
                                    <DeleteIcon
                                        fontSize="small"
                                        color="error"
                                    />
                                </ListItemIcon>
                                <Typography variant="inherit" color="error">
                                    {translate('buttons.delete')}
                                </Typography>
                            </MenuItem>
                        )}
                </Menu>
            )}
        </>
    )
}
